import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validateResponse } from "@/api/validateResponse";
import {
  MembershipPortalAddonListSchema,
  MembershipPortalAddonOptionsSchema,
  MembershipPortalAddonPurchaseResponseSchema,
  MembershipPortalMeSchema,
  type MembershipPortalAddonOptions,
  type MembershipPortalAddonPurchaseResponse,
  type MembershipPortalMe,
  type PurchaseMembershipPortalAddonHoursInput,
} from "@/api/types/membership";

/**
 * Dedicated axios instance for the public MEMBERSHIP portal — same
 * rationale as usePortal.ts's `portalClient` (never the staff `apiClient`,
 * which redirects to /login on 401; an expired/invalid membership-portal
 * link is a normal customer-facing outcome here too). Separate base path
 * from the event-portal client since it's a distinct token/backend
 * surface (app/api/portal_membership.py, mounted at
 * /api/public/portal/membership).
 */
const membershipPortalClient = axios.create({
  baseURL: "/api/public/portal/membership",
  headers: { "Content-Type": "application/json" },
});

export const membershipPortalKeys = {
  me: (token: string | null) => ["membership-portal", "me", token] as const,
  addonOptions: (token: string | null) => ["membership-portal", "addon-options", token] as const,
  addonList: (token: string | null) => ["membership-portal", "addon-list", token] as const,
};

export function useMembershipPortalMe(token: string | null) {
  return useQuery<MembershipPortalMe>({
    queryKey: membershipPortalKeys.me(token),
    queryFn: async () => {
      const { data } = await membershipPortalClient.get("/me", { params: { token } });
      return validateResponse(MembershipPortalMeSchema, data, "GET /public/portal/membership/me");
    },
    enabled: !!token,
    retry: false,
    staleTime: 30 * 1000,
  });
}

/**
 * Self-service add-on studio hours (migration 009). `available: false` /
 * `rate_cents_per_hour: null` means this member's tier has no add-on rate
 * configured — MembershipPortalPage.tsx renders nothing in that case (see
 * that component). This is the ONLY hook in the codebase that fetches
 * `rate_cents_per_hour` — it is token-scoped like every other hook in this
 * file, never callable without a valid membership-portal token, and must
 * never be reused from a staff-facing component (see
 * app/models/membership_tier.py's docstring for why).
 */
export function useMembershipPortalAddonOptions(token: string | null) {
  return useQuery<MembershipPortalAddonOptions>({
    queryKey: membershipPortalKeys.addonOptions(token),
    queryFn: async () => {
      const { data } = await membershipPortalClient.get("/addon-hours/options", { params: { token } });
      return validateResponse(
        MembershipPortalAddonOptionsSchema,
        data,
        "GET /public/portal/membership/addon-hours/options",
      );
    },
    enabled: !!token,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function useMembershipPortalAddonList(token: string | null) {
  return useQuery({
    queryKey: membershipPortalKeys.addonList(token),
    queryFn: async () => {
      const { data } = await membershipPortalClient.get("/addon-hours", { params: { token } });
      return validateResponse(MembershipPortalAddonListSchema, data, "GET /public/portal/membership/addon-hours").items;
    },
    enabled: !!token,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function useMembershipPortalPurchaseAddonHours(token: string | null) {
  const queryClient = useQueryClient();
  return useMutation<MembershipPortalAddonPurchaseResponse, unknown, PurchaseMembershipPortalAddonHoursInput>({
    mutationFn: async (input) => {
      const { data } = await membershipPortalClient.post("/addon-hours", input, { params: { token } });
      return validateResponse(
        MembershipPortalAddonPurchaseResponseSchema,
        data,
        "POST /public/portal/membership/addon-hours",
      );
    },
    onSuccess: () => {
      // Balance in /me and the addon list both change as a result of a
      // purchase — refetch rather than hand-patch the cache, same as
      // every other mutation hook in this codebase's simplest form.
      void queryClient.invalidateQueries({ queryKey: membershipPortalKeys.me(token) });
      void queryClient.invalidateQueries({ queryKey: membershipPortalKeys.addonList(token) });
    },
  });
}
